import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import classNames from 'classnames';
import {useAuthorizationState} from '../../contexts/Authorization';
import {graphqlWhoamiNextConditionalQuery} from '../../components/graphql';
import Button from '../../components/button/button';
import Skeleton from '../../components/Skeleton';
import ForMePostsList from '../vibe/posts/for-me-posts-list';
import {Link, useLocation} from 'react-router-dom';
import searchFilter from '../../searchFilter';
import {getVibeProfileSlug} from '../vibe/utilities/get-vibe-profile-slug';

import PostItem from '../vibe/posts/post-item/post-item';

const ForMeAnonymousPostsList = React.memo(({queryRef, currentUserId, isAuthorized, highlightId}) => {
    const {data} = usePaginationFragment(
        graphql`
            fragment vibeForMeAnonymousPostsList_query on Query
            @refetchable(queryName: "VibeForMeAnonymousPostsListQuery")
            @argumentDefinitions(
                first: {type: "Int", defaultValue: 8}
                after: {type: "String"}
            ) {
                forMeAnonymousPosts(first: $first, after: $after) @connection(key: "ForMeAnonymousPostsList_forMeAnonymousPosts") {
                    edges {
                        node {
                            id
                            message
                            media
                            created
                            removed
                            spam
                            likesCount
                            stemLikesCount
                            viewsCount
                            commentsCount
                            quotesCount
                            sharesCount
                            poll {
                                id
                                options
                                optionVotesCounts
                                pollVotesCount
                                votedByCurrentUser
                                votedOptionIndex
                            }
                            sharedPost {
                                id
                                message
                                media
                                created
                                removed
                                spam
                                likesCount
                                stemLikesCount
                                viewsCount
                                commentsCount
                                quotesCount
                                sharesCount
                                poll {
                                    id
                                    options
                                    optionVotesCounts
                                    pollVotesCount
                                    votedByCurrentUser
                                    votedOptionIndex
                                }
                                author {
                                    id
                                    vibe {
                                        username
                                    }
                                }
                            }
                            quotedPost {
                                id
                                message
                                media
                                created
                                removed
                                spam
                                likesCount
                                stemLikesCount
                                viewsCount
                                commentsCount
                                quotesCount
                                sharesCount
                                poll {
                                    id
                                    options
                                    optionVotesCounts
                                    pollVotesCount
                                    votedByCurrentUser
                                    votedOptionIndex
                                }
                                author {
                                    id
                                    vibe {
                                        username
                                    }
                                }
                            }
                            author {
                                id
                                vibe {
                                    username
                                }
                            }
                        }
                    }
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                }
            }
        `,
        queryRef
    );
    
    const [isPending] = React.useTransition();
    
    const posts = data.forMeAnonymousPosts?.edges || [];
    const postRefs = React.useRef(new Map());

    const getPostRef = React.useCallback((postId) => {
        if (!postRefs.current.has(postId)) {
            postRefs.current.set(postId, React.createRef());
        }
        return postRefs.current.get(postId);
    }, []);

    return (
        <div className={classNames({'opacity-0dot4': isPending})}>
            {posts.length === 0 && (
                <div className='text-md color-gray-500 padding-left-0dot75rem'>
                    <FormattedMessage defaultMessage='No posts for you yet' />
                </div>
            )}
            {posts.length > 0 && (
                <div>
                    {posts.map((edge, index) => {
                        const post = edge.node;
                        return (
                            <div key={post.id} className={index > 0 ? 'padding-top-1rem' : ''}>
                                <PostItem
                                    post={post}
                                    userId={post.author?.id}
                                    currentUserId={currentUserId}
                                    isAuthorized={isAuthorized}
                                    isFollowing={false}
                                    getPostRef={getPostRef}
                                    highlightId={highlightId}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

const homeVibeFeedQuery = graphql`
    query vibeHomePagesForMeFeedQuery(
        $includeIsAuthorized: Boolean!
        $first: Int!
        $after: String
    ) {
        ...forMePostsList_query
            @arguments(
                first: $first
                after: $after
                includeIsAuthorized: $includeIsAuthorized
            )
    }
`;

const HomeVibeAuthorized = () => {
    const location = useLocation();
    // Fetch whoamiNext separately - Relay will cache and reuse for other components
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextConditionalQuery, {
        includeWhoamiNext: true
    });

    const queryData = useLazyLoadQuery(
        homeVibeFeedQuery,
        {
            includeIsAuthorized: true,
            first: 8,
            after: null
        }
    );

    const currentUserId = whoamiNext?.id;
    const slug = whoamiNext ? getVibeProfileSlug({user: whoamiNext}) : null;
    const vibeLink = slug
        ? `/vibe/profiles/${slug}/for-me${searchFilter(location.search)}`
        : `/vibe/callback${searchFilter(location.search)}`;

    return (
        <div className='margin-top-1dot5rem-2rem'>
            <div className='display-flex justify-content-center'>
                <div className='position-relative mw768-max-width-43dot5rem width-100percent'>
                    <ForMePostsList
                        queryRef={queryData}
                        currentUserId={currentUserId}
                        isAuthorized={true}
                        highlightId={null}
                        nextCount={8}
                        showMoreButton={false}
                    />
                    <div className='padding-top-1rem display-flex justify-content-center'>
                        <Link to={vibeLink} className='text-decoration-none'>
                            <Button size='xl' color='secondary-gray'>
                                <FormattedMessage defaultMessage='More vibe' />
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

const homeVibeAnonymousFeedQuery = graphql`
    query vibeHomePagesForMeAnonymousFeedQuery($first: Int!, $after: String) {
        ...vibeForMeAnonymousPostsList_query @arguments(first: $first, after: $after)
    }
`;

const HomeVibeAnonymous = () => {
    const location = useLocation();
    const queryData = useLazyLoadQuery(
        homeVibeAnonymousFeedQuery,
        {
            first: 8,
            after: null
        }
    );

    return (
        <div className='margin-top-1dot5rem-2rem'>
            <div className='display-flex justify-content-center'>
                <div className='position-relative mw768-max-width-43dot5rem width-100percent'>
                    <ForMeAnonymousPostsList
                        queryRef={queryData}
                        currentUserId={null}
                        isAuthorized={false}
                        highlightId={null}
                    />
                    <div className='padding-top-1rem display-flex justify-content-center'>
                        <Link to={`/vibe/callback${searchFilter(location.search)}`} className='text-decoration-none'>
                            <Button size='xl' color='secondary-gray'>
                                <FormattedMessage defaultMessage='More vibe' />
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HomeVibe = () => {
    const authorized = useAuthorizationState();
    const location = useLocation();
    
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextConditionalQuery, {
        includeWhoamiNext: authorized
    });

    const slug = whoamiNext ? getVibeProfileSlug({user: whoamiNext}) : null;
    const myProfileLink = slug
        ? `/vibe/profiles/${slug}/for-me${searchFilter(location.search)}`
        : `/vibe/callback${searchFilter(location.search)}`;

    return (
        <div className='background-color-gray-50 padding-top-4rem mw768-padding-top-6rem'>
            <div className='view'>
                <div className='block'>
                    <div className='display-flex align-items-baseline column-gap-1rem'>
                        <div className='display-sm mw768-display-lg semibold color-gray-900'>
                            {/* Vibe */}
                            <FormattedMessage defaultMessage='Vibe' />
                        </div>
                        <Link
                            to={myProfileLink}
                            className='text-sm color-gray-700 text-decoration-underline'
                        >
                            <FormattedMessage defaultMessage='My Vibe' />
                        </Link>
                    </div>
                    <React.Suspense fallback={
                        <div className='margin-top-1dot5rem-2rem'>
                            <div className='display-flex justify-content-center'>
                                <div className='position-relative mw768-max-width-43dot5rem width-100percent'>
                                    <Skeleton />
                                </div>
                            </div>
                        </div>
                    }>
                        {authorized ? <HomeVibeAuthorized /> : <HomeVibeAnonymous />}
                    </React.Suspense>
                </div>
            </div>
        </div>
    );
};

export default React.memo(HomeVibe);

